/* eslint-disable newline-per-chained-call */
/* eslint-disable max-len */
/* global window */
/* eslint-disable no-use-before-define */
import { get } from 'lodash';
import Types from './Types';
import { requestXrefByVenueXids } from './xrefActions';

const { format } = window;

const researchIdMap = {
	wgrResearchHistory: 307,
	cibcResearchHistory: 248
};

const sortDirMap = {
	desc: 'desc',
	asc: 'asc'
};

const operatorMap = {
	equalTo: 'EqualTo',
	notEqualTo: 'NotEqualTo',
	prefix: 'Prefix'
};

const argsOperatorMap = {
	and: 'AND',
	or: 'OR'
};

const analystDefaultArgs = () => {
	return {
		wsodcompanies: [],
		keywords: [],
		source: ['all'],
		industries: null,
		dateStart: undefined,
		dateEnd: undefined,
		days: 30,
		periodRadio: 'predefined',
		periodRange: '1month',
		symbolPills: null,
		keywordsMatchType: 'exact',
		keywordsSearchIn: 'headlines',
		page: 0,
		resultsPerPage: 50,
		sortBy: 'DocumentDate',
		sortDir: 'desc'
	};
};

const sourceMap = {
	cibcWorldMarkets: ['CIBCWMEquities', 'CIBCWMEconomics', 'CIBCReportsOverviewWM'],
	morningstarAnalyst: ['MorningstarAnalyst'],
	morningstarQuant: ['MorningstarQuantUS', 'MorningstarQuantCA'],
	trStockReports: ['TRStockReportsPlus']
};

const getAnalystSearchSourcesMapByTier = tier => {
	const menuItems = { ...sourceMap };
	const { code } = tier || {};

	switch (code) {
		case 'IEE':
		case 'IEI':
		case 'PEE':
			delete menuItems.morningstarQuant;
			delete menuItems.trStockReports;
			break;
		case 'IISE':
		case 'IISI':
		case 'CPICE':
		case 'CPICI':
		case 'WGE':
		case 'WGI':
			delete menuItems.morningstarAnalyst;
			delete menuItems.morningstarQuant;
			delete menuItems.trStockReports;
			break;
		default:
			delete menuItems.morningstarAnalyst;
			delete menuItems.morningstarQuant;
			delete menuItems.trStockReports;
			break;
	}

	return menuItems;
};

const getAllAnalystSearchSourcesByTier = tier => {
	const available = getAnalystSearchSourcesMapByTier(tier);
	const sources = [];
	Object.keys(available).forEach(key => {
		available[key].forEach(s => sources.push(s));
	});
	return sources;
};

const getSourceMenuItemsByTier = tier => {
	const menuItems = { ...sourceMap };
	const { code } = tier || {};

	switch (code) {
		case 'IEE':
		case 'IEI':
		case 'PEE':
			break;
		case 'IISE':
		case 'IISI':
		case 'WGE':
		case 'WGI':
			delete menuItems.morningstarAnalyst;
			delete menuItems.morningstarQuant;
			delete menuItems.trStockReports;
			break;
		default:
			delete menuItems.morningstarAnalyst;
			delete menuItems.morningstarQuant;
			delete menuItems.trStockReports;
			break;
	}

	return menuItems;
};

const addSourcesByTier = (inputs, source, tier) => {
	let sources = [];
	const { code } = tier || {};

	if (source && source.length && source[0] !== 'all') {
		sources = sources.concat(source);
	} else {
		// all tiers get these sources
		sources = sources.concat(sourceMap.cibcWorldMarkets);

		switch (code) {
			case 'IEE':
			case 'IEI':
			case 'PEE':
				sources = sources.concat(sourceMap.morningstarAnalyst,
					sourceMap.morningstarQuant,
					sourceMap.trStockReports);
				break;
			default:
				break;
		}
	}

	inputs.arguments.push({
		field: 'Module',
		type: 'string',
		conditions: [
			{
				operator: operatorMap.equalTo,
				values: sources
			}
		]
	});

	return inputs;
};

const getMorningstarReportsArgs = xid => {
	return {
		argsOperator: argsOperatorMap.and,
		arguments: [
			{
				field: 'Module',
				type: 'string',
				conditions: [
					{
						operator: operatorMap.equalTo,
						values: [
							'MStarETFResearch'
						]
					}
				]
			},
			{
				field: 'WSODIssue',
				type: 'string',
				conditions: [
					{
						operator: operatorMap.equalTo,
						values: [
							`${xid}`
						]
					}
				]
			}
		],
		sortArgument: {
			direction: sortDirMap.desc,
			field: 'DocumentDate'
		},
		limit: 1,
		offset: 0
	};
};

const getMorningstarAnalystCommentary = (offset = 0) => {
	return {
		argsOperator: argsOperatorMap.or,
		arguments: [
			{
				field: 'Module',
				type: 'string',
				conditions: [
					{
						operator: operatorMap.equalTo,
						values: [
							'MStarETFCommentary'
						]
					}
				]
			},
			{
				field: 'ReportType',
				type: 'string',
				conditions: [
					{
						operator: operatorMap.equalTo,
						values: [
							'ETF Weekly',
							'CAN ETF Investor Insight',
							'ETF Specialist'
						]
					}
				]
			}
		],
		sortArgument: {
			direction: sortDirMap.desc,
			field: 'DocumentDate'
		},
		limit: 10,
		offset
	};
};

const getResearchBySingleFieldValue = (
	field,
	value,
	limit = 1,
	offset = 0,
	sortField = 'DocumentDate',
	sortDirection = sortDirMap.desc,
	cutoffDays = 1000
) => {
	const query = {
		argsOperator: argsOperatorMap.and,
		arguments: [
			{
				field,
				type: 'string',
				conditions: [
					{
						operator: operatorMap.equalTo,
						values: [
							value
						]
					}
				]
			}
		],
		sortArgument: {
			direction: sortDirection,
			field: sortField
		},
		limit,
		offset
	};

	if (cutoffDays !== 1000) {
		const startDate = format.moment().utc().add(-cutoffDays, 'days').startOf('day').format();
		query.arguments.push({
			field: 'DocumentDate',
			type: 'date',
			startDate
		});
	}

	return query;
};

const getResearchHistoryArgs = (xids, count, offset = 0) => {
	return {
		argsOperator: argsOperatorMap.and,
		arguments: [
			{
				field: 'PrimaryWSODIssue',
				type: 'number',
				conditions: [
					{
						operator: operatorMap.equalTo,
						values: xids
					}
				]
			},
			{
				field: 'DocumentDate',
				type: 'date',
				startDate: format.moment().subtract(1, 'years').utc().startOf('day').format(),
				endDate: format.moment().utc().endOf('day').format()

			}
		],
		sortArgument: {
			direction: sortDirMap.desc,
			field: 'DocumentDate'
		},
		limit: count,
		offset
	};
};
/**
 *
 * @param {array} xids an array of venueXids
 * @param {number} count numer of results to return
 * @param {number} offset for pagination purposes
 * @param {string[]} source array of sources from which to query
 * @param {boolean} isPrimary
 * @param {*} sortBy
 * @param {*} sortDir
 * @param {string[]} keywordText array of keywords w/ which to search
 * NOTE: only pass either days, or (startDate and endDate);
 *       startDate/endDate take precedence over days if all are passed
 * @param {number} days number of days to subtract from current day, used as start date
 * @param {string} startDate must be in ISO 8601 date format
 * @param {string} endDate must be in ISO 8601 date format
 */
const getStockReportsHistoryArgs = ({
	xids,
	count,
	offset,
	days,
	source,
	isPrimary,
	sortBy = 'DocumentDate',
	sortDir = sortDirMap.desc,
	tier,
	keywordText,
	keywordsHeadlinesOnly,
	industries,
	startDate,
	endDate,
	keywordsMatchType
}) => {
	let inputs = {
		argsOperator: argsOperatorMap.and,
		arguments: [],
		sortArgument: {
			direction: sortDir,
			field: sortBy
		},
		limit: count,
		offset
	};

	inputs = addSourcesByTier(inputs, source, tier);
	inputs.arguments.push({
		field: isPrimary ? 'PrimaryWSODIssue' : 'WSODIssue',
		type: 'string',
		conditions: [
			{
				operator: operatorMap.equalTo,
				values: xids.map(xid => `${xid}`)
			}
		]
	});

	if (days !== 1000) {
		// default to 'today'
		const todayDateBegin = format.moment().utc().startOf('day').format();
		const todayDateEnd = format.moment().utc().endOf('day').format();

		const dateArgObj = {
			field: 'DocumentDate',
			type: 'date',
			startDate: todayDateBegin
		};

		if (startDate && endDate) {
			dateArgObj.startDate = new Date(startDate).toISOString();
			dateArgObj.endDate = new Date(endDate).toISOString();
		} else if (days !== undefined) {
			if (days === 0 || days === 1) {
				dateArgObj.startDate = todayDateBegin;
			} else {
				const tempStartDate = format.moment().utc().add(-days, 'days').startOf('day').format();
				dateArgObj.startDate = tempStartDate;
				dateArgObj.endDate = todayDateEnd;
			}
		}

		inputs.arguments.push(dateArgObj);
	}

	if (keywordText && keywordText.length > 0) {
		let keywords = keywordText;
		if (keywordsMatchType && keywordsMatchType === 'exact') {
			keywords = [keywordText.join(' ')];
		}

		inputs.arguments.push({
			field: keywordsHeadlinesOnly ? 'Headline' : 'FreeText',
			type: 'string',
			conditions: [
				{
					operator: operatorMap.equalTo,
					values: keywords
				}
			]
		});
	}

	if (industries && industries.length > 0) {
		// loop through industry and wrap in extra quotes b/c API requirement
		const quotedIndustries = [];
		industries.forEach(ind => {
			quotedIndustries.push(`"${ind}"`);
		});

		inputs.arguments.push({
			field: 'Industry',
			type: 'string',
			conditions: [
				{
					operator: operatorMap.equalTo,
					values: quotedIndustries
				}
			]
		});
	}

	return inputs;
};

const setCustomSearchInputs = customSearchInputs => {
	return ({
		type: Types.SET_CUSTOM_SEARCH_INPUTS,
		customSearchInputs
	});
};

const receiveResearchHistoryWithMetaData = (response, documentId) => {
	format.removeUtcTimezoneFromProperty(get(response, 'data'), 'dateTime');
	return ({
		type: Types.API_RECEIVE_RESEARCH_HISTORY_META_DATA,
		response,
		documentId
	});
};

const requestResearchHistoryDocumentMetaData = (researchId, documentIdAndVersionString, responseHandler) => {
	return ({
		type: Types.API_REQUEST_RESEARCH_HISTORY_META_DATA,
		endPoint: `/research/v1/${researchId}/documents/${documentIdAndVersionString}`,
		ajaxType: 'GET',
		onSuccess: responseHandler
			|| (response => { return receiveResearchHistoryWithMetaData(response, documentIdAndVersionString); }),
		loader: handleLoader,
		fetchName: 'fetchingResearchHistoryDocumentMetaData'
	});
};

const receiveResearchHistory = (response, researchId, getMetaData) => {
	const actions = [];
	const metaActions = [];
	const xids = [];
	if (response && response.data && response.data.items) {
		response.data.items.forEach(item => {
			const { documentId, Companies, VersionTag } = item;
			const { Company } = Companies || {};

			if (getMetaData) {
				metaActions.push(requestResearchHistoryDocumentMetaData(researchId, `${documentId}-${VersionTag}`));
			}

			if (Company && Company.length > 0) {
				Company.forEach(c => {
					if (c.WSODIssue && xids.indexOf(c.WSODIssue) === -1) {
						xids.push(c.WSODIssue);
					}
				});
			}
		});
	}

	if (xids.length > 0) {
		const chunk = 40;
		for (let i = 0; i < xids.length; i += chunk) {
			const tempXids = xids.slice(i, i + chunk);
			// breaking the xref calls into chunks of 40
			actions.push(requestXrefByVenueXids(tempXids));
		}
	}

	actions.push(...metaActions);
	format.removeUtcTimezoneFromPropertyOnCollection(get(response, 'data.items'), 'DocumentDate');

	actions.push({
		type: Types.API_RECEIVE_RESEARCH_HISTORY,
		response
	});

	return actions;
};

const requestResearchHistory = (researchId, researchInputs, getMetaData = false, responseHandler) => {
	const result = {
		type: Types.API_REQUEST_RESEARCH_HISTORY,
		endPoint: `/research/v1/${researchId}/documents`,
		params: researchInputs,
		ajaxType: 'POST',
		onSuccess: responseHandler
			|| (response => { return receiveResearchHistory(response, researchId, getMetaData); }),
		on404: responseHandler || receiveResearchHistory,
		loader: handleLoader,
		fetchName: 'fetchingResearchHistoryData'
	};
	return result;
};

const receiveFileSize = response => {
	let fileSize;
	if (response.data.links && response.data.links[0]) {
		fileSize = response.data.links[0].fileSize;
	}

	return ({
		type: Types.API_RECEIVE_RESEARCH_FILE_SIZE,
		fileSize
	});
};

const requestResearchHistoryLink = (researchId, documentId) => {
	return ({
		type: Types.API_REQUEST_RESEARCH_HISTORY_LINK,
		endPoint: `research/v1/${researchId}/documents/${documentId}`,
		ajaxType: 'GET',
		onSuccess: requestResearchHistoryDocument,
		loader: handleLoader,
		fetchName: 'fetchingResearchHistoryLink'
	});
};

const requestFileSize = (researchId, documentId) => {
	return ({
		type: Types.API_REQUEST_RESEARCH_FILE_SIZE,
		endPoint: `research/v1/${researchId}/documents/${documentId}`,
		ajaxType: 'GET',
		onSuccess: receiveFileSize,
		loader: handleLoader,
		fetchName: 'fetchingResearchHistoryLink'
	});
};

const requestResearchHistoryDocument = response => {
	let href;
	let accept;

	if (response.data.links && response.data.links[0]) {
		href = response.data.links[0].href;
		accept = response.data.links[0].headerSchema.Accept;
	}

	return ({
		type: Types.API_REQUEST_RESEARCH_HISTORY_DOCUMENT,
		endPoint: `/research${href}`,
		headers: {
			ACCEPT: accept
		},
		ajaxType: 'GETPDF',
		onSuccess: receiveFileSize,
		loader: handleLoader,
		fetchName: 'fetchingResearchHistoryDocument'
	});
};

const requestPdf = (researchId, documentId) => {
	return ({
		type: Types.API_REQUEST_RESEARCH_PDF,
		endPoint: `/research/v1/${researchId}/documents/${documentId}/component-type/PRES`,
		documentName: documentId,
		headers: {
			ACCEPT: 'application/pdf'
		},
		ajaxType: 'GETPDF',
		loader: handleLoader,
		fetchName: 'fetchingResearchHistoryDocument'
	});
};

const handleLoader = fetchName => {
	return ({
		type: Types.API_HANDLE_RESEARCH_HISTORY_LOADER,
		fetchName
	});
};

const sourceToFeedMap = {
	'CIBC World Markets': [2776],
	'Morningstar Analyst': [2039],
	'Morningstar Quant': [2092, 2647],
	'S&P': [1949],
	CFRA: [1949],
	'TR Profiles': [2415],
	'TR StockReports+': [2447]
};

const feedToSourceMap = () => {
	const map = {};
	Object.keys(sourceToFeedMap).forEach(key => {
		sourceToFeedMap[key].forEach(feed => {
			map[feed] = key;
		});
	});
	return map;
};

const sourceFromFeed = feed => {
	const map = feedToSourceMap();
	const source = map[feed] ? map[feed] : '--';
	return source;
};

const getDaysFromDateRange = rangeCopy => {
	let days = 30;
	switch (rangeCopy) {
		case 'New':
			days = 0;
			break;
		case 'Today':
			days = 1;
			break;
		case '2 Days':
			days = 2;
			break;
		case '1 Week':
			days = 7;
			break;
		case '1 Month':
			days = 30;
			break;
		case '2 Months':
			days = 60;
			break;
		case '3 Months':
			days = 90;
			break;
		case '6 Months':
			days = 180;
			break;
		case '1 Year':
			days = 365;
			break;
		case 'All':
			days = 1000;
			break;
		case 'Custom Range':
		default:
			days = null;
			break;
	}
	return days;
};

const getDateRangeFromDays = days => {
	let range = 'Custom Range';
	switch (days) {
		case 0:
			range = 'New';
			break;
		case 1:
			range = 'Today';
			break;
		case 2:
			range = '2 Days';
			break;
		case 7:
			range = '1 Week';
			break;
		case 30:
			range = '1 Month';
			break;
		case 60:
			range = '2 Months';
			break;
		case 90:
			range = '3 Months';
			break;
		case 180:
			range = '6 Months';
			break;
		case 365:
			range = '1 Year';
			break;
		case 1000:
		default:
			range = 'All';
			break;
	}
	return range;
};

export {
	researchIdMap,
	requestResearchHistory,
	requestResearchHistoryLink,
	getMorningstarReportsArgs,
	getResearchHistoryArgs,
	getDaysFromDateRange,
	requestPdf,
	sourceFromFeed,
	getStockReportsHistoryArgs,
	sortDirMap,
	getSourceMenuItemsByTier,
	getAllAnalystSearchSourcesByTier,
	getAnalystSearchSourcesMapByTier,
	getMorningstarAnalystCommentary,
	getResearchBySingleFieldValue,
	requestResearchHistoryDocumentMetaData,
	setCustomSearchInputs,
	analystDefaultArgs,
	getDateRangeFromDays,
	requestFileSize,
	sourceMap
};
